/**
 * Scenario Context
 *
 * Manages scenario state for the sandbox feature,
 * including create, save, run, and compare operations.
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

// Scenario types (matching database schema)
export interface Scenario {
  id: string;
  clientId: string;
  name: string;
  scenarioType: 'launch' | 'pricing' | 'portfolio' | 'market';
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  isBaseline: boolean;
  version: number;
  status: 'draft' | 'running' | 'completed' | 'failed';
}

export interface ScenarioResult {
  id: string;
  scenarioId: string;
  computedAt: string;
  results: Record<string, unknown>;
  metrics: Record<string, number | string>;
}

export interface ScenarioWithResult extends Scenario {
  latestResult?: ScenarioResult;
}

interface ScenarioContextType {
  // State
  scenarios: ScenarioWithResult[];
  activeScenario: ScenarioWithResult | null;
  comparisonScenarios: ScenarioWithResult[];
  loading: boolean;
  error: string | null;

  // Actions
  loadScenarios: (clientId: string) => Promise<void>;
  createScenario: (clientId: string, data: Partial<Scenario>) => Promise<Scenario>;
  updateScenario: (id: string, data: Partial<Scenario>) => Promise<void>;
  deleteScenario: (id: string) => Promise<void>;
  duplicateScenario: (id: string, newName: string) => Promise<Scenario>;
  runScenario: (id: string) => Promise<ScenarioResult>;

  // Selection
  setActiveScenario: (scenario: ScenarioWithResult | null) => void;
  addToComparison: (scenario: ScenarioWithResult) => void;
  removeFromComparison: (id: string) => void;
  clearComparison: () => void;
}

const ScenarioContext = createContext<ScenarioContextType | null>(null);

export function ScenarioProvider({ children }: { children: ReactNode }) {
  const [scenarios, setScenarios] = useState<ScenarioWithResult[]>([]);
  const [activeScenario, setActiveScenario] = useState<ScenarioWithResult | null>(null);
  const [comparisonScenarios, setComparisonScenarios] = useState<ScenarioWithResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadScenarios = useCallback(async (clientId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/clients/${clientId}/scenarios`);
      if (!response.ok) throw new Error('Failed to load scenarios');
      const data = await response.json();
      setScenarios(data.scenarios || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setScenarios([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const createScenario = useCallback(async (clientId: string, data: Partial<Scenario>) => {
    setError(null);
    try {
      const response = await fetch(`/api/clients/${clientId}/scenarios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to create scenario');
      const newScenario = await response.json();
      setScenarios(prev => [...prev, newScenario]);
      return newScenario;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    }
  }, []);

  const updateScenario = useCallback(async (id: string, data: Partial<Scenario>) => {
    setError(null);
    try {
      const scenario = scenarios.find(s => s.id === id);
      if (!scenario) throw new Error('Scenario not found');

      const response = await fetch(`/api/clients/${scenario.clientId}/scenarios/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to update scenario');
      const updated = await response.json();
      setScenarios(prev => prev.map(s => s.id === id ? updated : s));

      if (activeScenario?.id === id) {
        setActiveScenario(updated);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    }
  }, [scenarios, activeScenario]);

  const deleteScenario = useCallback(async (id: string) => {
    setError(null);
    try {
      const scenario = scenarios.find(s => s.id === id);
      if (!scenario) throw new Error('Scenario not found');

      const response = await fetch(`/api/clients/${scenario.clientId}/scenarios/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete scenario');

      setScenarios(prev => prev.filter(s => s.id !== id));
      if (activeScenario?.id === id) {
        setActiveScenario(null);
      }
      setComparisonScenarios(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    }
  }, [scenarios, activeScenario]);

  const duplicateScenario = useCallback(async (id: string, newName: string) => {
    setError(null);
    try {
      const scenario = scenarios.find(s => s.id === id);
      if (!scenario) throw new Error('Scenario not found');

      const response = await fetch(`/api/clients/${scenario.clientId}/scenarios/${id}/duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName }),
      });
      if (!response.ok) throw new Error('Failed to duplicate scenario');
      const newScenario = await response.json();
      setScenarios(prev => [...prev, newScenario]);
      return newScenario;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    }
  }, [scenarios]);

  const runScenario = useCallback(async (id: string) => {
    setError(null);
    try {
      const scenario = scenarios.find(s => s.id === id);
      if (!scenario) throw new Error('Scenario not found');

      // Update status to running
      setScenarios(prev => prev.map(s =>
        s.id === id ? { ...s, status: 'running' as const } : s
      ));

      const response = await fetch(`/api/clients/${scenario.clientId}/scenarios/${id}/run`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to run scenario');
      const result = await response.json();

      // Update scenario with result
      setScenarios(prev => prev.map(s =>
        s.id === id ? { ...s, status: 'completed' as const, latestResult: result } : s
      ));

      if (activeScenario?.id === id) {
        setActiveScenario(prev => prev ? { ...prev, status: 'completed', latestResult: result } : null);
      }

      return result;
    } catch (err) {
      setScenarios(prev => prev.map(s =>
        s.id === id ? { ...s, status: 'failed' as const } : s
      ));
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    }
  }, [scenarios, activeScenario]);

  const addToComparison = useCallback((scenario: ScenarioWithResult) => {
    setComparisonScenarios(prev => {
      if (prev.find(s => s.id === scenario.id)) return prev;
      if (prev.length >= 3) {
        // Replace the oldest one
        return [...prev.slice(1), scenario];
      }
      return [...prev, scenario];
    });
  }, []);

  const removeFromComparison = useCallback((id: string) => {
    setComparisonScenarios(prev => prev.filter(s => s.id !== id));
  }, []);

  const clearComparison = useCallback(() => {
    setComparisonScenarios([]);
  }, []);

  return (
    <ScenarioContext.Provider
      value={{
        scenarios,
        activeScenario,
        comparisonScenarios,
        loading,
        error,
        loadScenarios,
        createScenario,
        updateScenario,
        deleteScenario,
        duplicateScenario,
        runScenario,
        setActiveScenario,
        addToComparison,
        removeFromComparison,
        clearComparison,
      }}
    >
      {children}
    </ScenarioContext.Provider>
  );
}

export function useScenarios() {
  const context = useContext(ScenarioContext);
  if (!context) {
    throw new Error('useScenarios must be used within a ScenarioProvider');
  }
  return context;
}
